import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Copy,
  FileCheck2,
  HardDrive,
  Package,
  Plus,
  RefreshCw,
  Smartphone,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PublishDialog } from "../components/PublishDialog.js";
import { copyText } from "../lib/clipboard.js";
import { actorName, daysAgo, dayString, formatBytes, formatDateTime } from "../lib/format.js";
import { useScope } from "../lib/scope.js";
import {
  useActiveUsersQuery,
  useAdoptionQuery,
  useListAppsQuery,
  useListAuditQuery,
  useListReleasesQuery,
  useOverviewQuery,
} from "../store/api.js";
import { useAppSelector } from "../store/index.js";
import { Button } from "@/components/ui/button";

const PIE_COLORS = ["#2563EB", "#17B26A", "#F79009", "#854D0E", "#64748B"];

export function OverviewPage() {
  const role = useAppSelector((state) => state.auth.user?.role);
  const canRelease = role === "admin" || role === "releaser";

  const scope = useScope();
  const apps = useListAppsQuery();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [copiedHash, setCopiedHash] = useState(false);

  const selectedApp = apps.data?.find((a) => a.slug === (scope.app || "digibikes")) ?? apps.data?.[0];
  const appSlug = selectedApp?.slug ?? "digibikes";
  const channel = scope.channel || undefined;

  const [from] = useState(daysAgo(14));
  const [to] = useState(dayString(new Date()));

  // 100% Real API queries from SQLite backend
  const overview = useOverviewQuery(
    { app: appSlug, channel, actor: "device" },
    { skip: !selectedApp }
  );

  const releases = useListReleasesQuery(
    { app: appSlug, channel, limit: 5, offset: 0 },
    { skip: !selectedApp }
  );

  const adoption = useAdoptionQuery(
    { app: appSlug, channel },
    { skip: !selectedApp }
  );

  const activeUsers = useActiveUsersQuery(
    { app: appSlug, channel, from, to, actor: "device" },
    { skip: !selectedApp }
  );

  const audit = useListAuditQuery({ limit: 5, offset: 0 });

  // Real active release from database
  const activeRelease = releases.data?.releases.find((r) => r.status === "active") ?? releases.data?.releases[0];

  function handleCopySha(sha: string) {
    void copyText(sha);
    setCopiedHash(true);
    setTimeout(() => setCopiedHash(false), 2000);
  }

  // Real version adoption data transformed for the donut chart
  const versionData = useMemo(() => {
    const rows = adoption.data?.rows ?? [];
    if (rows.length === 0) return [];
    return rows.map((r, i) => ({
      name: `v${r.nativeVersion} (${r.platform})`,
      devices: r.devices,
      share: Math.round(r.share * 1000) / 10,
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [adoption.data]);

  const activityPoints = activeUsers.data?.points ?? [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium text-[#667085] mb-1">
            <Clock className="size-3.5" />
            <span>{new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
            <span>·</span>
            <button
              type="button"
              onClick={() => {
                void overview.refetch();
                void releases.refetch();
                void adoption.refetch();
              }}
              className="inline-flex items-center gap-1 text-[#2563EB] hover:underline"
              title="Segarkan data dari server"
            >
              <RefreshCw className="size-3" />
              Segarkan
            </button>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[#101828]">
            Ringkasan Operasional OTA
          </h1>
          <p className="text-[13.5px] text-[#667085]">
            Aplikasi <strong className="text-[#101828] font-semibold">{selectedApp?.name ?? "Digibikes"}</strong>
            {scope.channel ? (
              <> · Kanal <strong className="text-[#101828] font-semibold">{scope.channel}</strong></>
            ) : (
              <> · Semua Kanal</>
            )}
          </p>
        </div>

        {canRelease && (
          <Button
            onClick={() => setPublishOpen(true)}
            className="h-9 gap-2 bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs font-medium shadow-xs"
          >
            <Plus className="size-4" />
            + Buat Rilis
          </Button>
        )}
      </div>

      {/* Live Status Banner (when active release exists) */}
      {activeRelease && !bannerDismissed && (
        <div className="flex items-center justify-between rounded-xl border border-[#ABEFC6] bg-[#ECFDF3] p-4 text-xs text-[#027A48] shadow-xs">
          <div className="flex items-center gap-3">
            <span className="flex size-2 rounded-full bg-[#17B26A] animate-pulse" />
            <div>
              <span className="font-semibold text-[#067647]">
                Rilis Aktif: v{activeRelease.nativeVersion} (#{activeRelease.releaseSeq})
              </span>
              <span className="text-[#05603A] ml-1.5">
                Kanal {activeRelease.channel} · Rollout {activeRelease.rolloutPercent}% · Berkas {formatBytes(activeRelease.sizeBytes)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="text-[#05603A] hover:text-[#067647] p-1"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      {/* 6 Real Stat Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {/* Total Perangkat Aktif */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Total Perangkat</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.devices.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Aktif 30 hari</span>
        </div>

        {/* Perangkat Aktif Hari Ini (DAU) */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Aktif Hari Ini (DAU)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#2563EB] font-mono">
            {overview.data?.dau.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Instalasi hari ini</span>
        </div>

        {/* Aktif Mingguan (WAU) */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Aktif 7 Hari (WAU)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.wau.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Trailing 7 hari</span>
        </div>

        {/* Aktif Bulanan (MAU) */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Aktif 30 Hari (MAU)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.mau.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Trailing 30 hari</span>
        </div>

        {/* Total Rilis */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Total Rilis</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {releases.data?.total.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Rilis terdaftar</span>
        </div>

        {/* Sesi 7 Hari */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Sesi (7 Hari)</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {overview.data?.sessions7d.toLocaleString("id-ID") ?? "0"}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Total pelaporan</span>
        </div>
      </div>

      {/* Rilis Aktif Saat Ini Card */}
      <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
        <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
          <div>
            <h3 className="text-sm font-bold text-[#101828]">Rilis Produksi Saat Ini</h3>
            <p className="text-xs text-[#667085]">
              Bundle OTA aktif yang saat ini dilayani ke perangkat {selectedApp?.name}
            </p>
          </div>
          <Link
            to="/releases"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline"
          >
            Lihat semua rilis <ArrowRight className="size-3.5" />
          </Link>
        </div>

        {activeRelease ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <span className="text-[11px] text-[#667085]">Versi & Urutan</span>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold font-mono text-[#101828]">
                  v{activeRelease.nativeVersion}
                </span>
                <span className="rounded bg-[#EFF4FF] px-2 py-0.5 text-xs font-mono font-medium text-[#2563EB]">
                  #{activeRelease.releaseSeq}
                </span>
                <span className="rounded bg-[#F6F7F9] px-2 py-0.5 text-xs font-mono capitalize text-[#475467]">
                  {activeRelease.platform}
                </span>
              </div>
              <span className="block text-[11px] text-[#98A2B3]">
                Dibuat {formatDateTime(activeRelease.createdAt)}
              </span>
            </div>

            <div className="space-y-1">
              <span className="text-[11px] text-[#667085]">Progres Rollout</span>
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 rounded-full bg-[#EAECF0] overflow-hidden">
                  <div
                    className="h-full bg-[#17B26A] rounded-full transition-all"
                    style={{ width: `${activeRelease.rolloutPercent}%` }}
                  />
                </div>
                <span className="font-mono text-xs font-bold text-[#101828]">
                  {activeRelease.rolloutPercent}%
                </span>
              </div>
              <span className="block text-[11px] text-[#667085]">
                Ukuran bundle: {formatBytes(activeRelease.sizeBytes)}
              </span>
            </div>

            <div className="space-y-1">
              <span className="text-[11px] text-[#667085]">Fingerprint SHA-256</span>
              <div className="flex items-start gap-2">
                <span className="font-mono text-xs text-[#344054] bg-[#F6F7F9] px-2 py-1 rounded break-all select-all leading-relaxed">
                  {activeRelease.sha256}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopySha(activeRelease.sha256)}
                  className="rounded p-1 text-[#667085] hover:text-[#101828] hover:bg-[#F6F7F9]"
                  title="Salin SHA-256"
                >
                  <Copy className="size-3.5" />
                </button>
                {copiedHash && <span className="text-[11px] text-[#17B26A]">Tersalin!</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 py-6 text-center">
            <Package className="mx-auto size-8 text-[#98A2B3]" />
            <p className="mt-2 text-xs font-medium text-[#101828]">Belum ada rilis aktif pada kanal ini</p>
            <p className="text-[11px] text-[#667085]">
              Unggah bundle zip baru untuk memulai deployment rilis OTA ke perangkat.
            </p>
            {canRelease && (
              <Button
                size="sm"
                onClick={() => setPublishOpen(true)}
                className="mt-3 h-8 bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs gap-1.5"
              >
                <Plus className="size-3.5" /> Unggah Rilis Baru
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Main Real Charts Row */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Real Activity Chart (2 cols) */}
        <div className="lg:col-span-2 rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
            <div>
              <h3 className="text-sm font-bold text-[#101828]">Tren Keaktifan Harian (14 Hari)</h3>
              <p className="text-xs text-[#667085]">Data riil DAU dan WAU perangkat dari database</p>
            </div>
            <Link to="/analytics" className="text-xs font-medium text-[#2563EB] hover:underline">
              Buka Analitik Penuh →
            </Link>
          </div>

          <div className="mt-4 h-[240px] w-full">
            {activityPoints.length === 0 ? (
              <div className="flex h-full items-center justify-center text-xs text-[#98A2B3]">
                Belum ada rekaman telemetri harian untuk periode ini.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activityPoints}>
                  <XAxis dataKey="day" stroke="#98A2B3" fontSize={11} />
                  <YAxis stroke="#98A2B3" fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="dau" name="DAU" stroke="#2563EB" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="wau" name="WAU" stroke="#17B26A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Real Version Distribution Donut Chart (1 col) */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
          <div className="pb-3 border-b border-[#EAECF0]">
            <h3 className="text-sm font-bold text-[#101828]">Distribusi Versi Terpasang</h3>
            <p className="text-xs text-[#667085]">Berdasarkan laporan heartbeat perangkat</p>
          </div>

          <div className="mt-4 flex flex-col items-center">
            {versionData.length === 0 ? (
              <div className="py-12 text-center text-xs text-[#98A2B3]">
                Belum ada instalasi perangkat yang melapor.
              </div>
            ) : (
              <>
                <div className="h-[180px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={versionData}
                        dataKey="devices"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={2}
                      >
                        {versionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-3 w-full space-y-1.5">
                  {versionData.slice(0, 4).map((entry) => (
                    <div key={entry.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="font-mono text-[#344054] truncate max-w-[120px]">{entry.name}</span>
                      </div>
                      <span className="font-mono font-medium text-[#101828]">
                        {entry.devices.toLocaleString("id-ID")} ({entry.share}%)
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row: Real Releases Table & Audit Logs */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Real Releases Table (2 cols) */}
        <div className="lg:col-span-2 rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
            <div>
              <h3 className="text-sm font-bold text-[#101828]">Daftar Rilis Terbaru</h3>
              <p className="text-xs text-[#667085]">5 rilis terakhir yang didaftarkan ke server</p>
            </div>
            <Link to="/releases" className="text-xs font-semibold text-[#2563EB] hover:underline">
              Kelola Rilis →
            </Link>
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-[#EAECF0] text-[#667085]">
                  <th className="pb-2">Seq</th>
                  <th className="pb-2">Versi Native</th>
                  <th className="pb-2">Platform</th>
                  <th className="pb-2">Kanal</th>
                  <th className="pb-2">Rollout</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F6F7F9]">
                {(releases.data?.releases.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-[#98A2B3]">
                      Belum ada rilis yang diterbitkan.
                    </td>
                  </tr>
                ) : (
                  releases.data?.releases.map((rel) => (
                    <tr key={rel.id} className="py-2.5">
                      <td className="py-2.5 font-mono font-semibold text-[#101828]">#{rel.releaseSeq}</td>
                      <td className="py-2.5 font-mono text-[#344054]">v{rel.nativeVersion}</td>
                      <td className="py-2.5 capitalize text-[#667085]">{rel.platform}</td>
                      <td className="py-2.5 capitalize text-[#667085]">{rel.channel}</td>
                      <td className="py-2.5 font-mono text-[#101828]">{rel.rolloutPercent}%</td>
                      <td className="py-2.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                            rel.status === "active"
                              ? "bg-[#ECFDF3] text-[#027A48]"
                              : rel.status === "rolled_back"
                              ? "bg-[#FEF3F2] text-[#B42318]"
                              : "bg-[#F6F7F9] text-[#667085]"
                          }`}
                        >
                          {rel.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Real Audit Log Timeline (1 col) */}
        <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
            <div>
              <h3 className="text-sm font-bold text-[#101828]">Aktivitas Log Audit</h3>
              <p className="text-xs text-[#667085]">Tindakan operator terbaru</p>
            </div>
            <Link to="/audit" className="text-xs font-semibold text-[#2563EB] hover:underline">
              Semua Log →
            </Link>
          </div>

          <div className="mt-4 space-y-3">
            {(audit.data?.entries.length ?? 0) === 0 ? (
              <p className="py-6 text-center text-xs text-[#98A2B3]">Belum ada catatan log audit.</p>
            ) : (
              audit.data?.entries.map((item) => (
                <div key={item.id} className="flex items-start gap-2.5 text-xs">
                  <div className="size-6 rounded-full bg-[#EFF4FF] flex items-center justify-center text-[10px] font-bold text-[#2563EB] shrink-0 mt-0.5">
                    {actorName(item.actorLabel).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1 leading-tight">
                    <span className="font-semibold text-[#101828] block truncate">
                      {actorName(item.actorLabel)}
                    </span>
                    <span className="font-mono text-[11px] text-[#475467] block truncate">
                      {item.action}
                    </span>
                    <span className="text-[10px] text-[#98A2B3] block mt-0.5">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {publishOpen && (
        <PublishDialog
          apps={apps.data ?? []}
          defaultChannel={scope.channel || "production"}
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}
